/**
 * sidebarPanelStore — Zustand store for the active sidebar panel.
 *
 * The SidebarMenu icon bar already tracks its own per-panel boolean state for
 * Tasks/Labs flyouts using React local state and an imperative ref handle.
 * For the People DB lightbox we need cross-component coordination — the
 * sidebar icon toggles it on, but OwnerCanvas owns the lightbox mount. A
 * store is the cleanest way for the two to agree on whether the panel is
 * open without threading another prop or ref API.
 *
 * Only the People (Orbit) lightbox uses this store. Other sidebar panels
 * remain on local React state to keep this change scoped — folding them into
 * the store can come in a follow-up if more cross-component panels appear.
 * Because there is a single `activePanel` slot, panels are mutually exclusive
 * for free.
 */
import { create } from "zustand";

export type SidebarPanel = "people" | null;

export interface SidebarPanelState {
  /** Which sidebar-driven overlay is currently active, if any. */
  activePanel: SidebarPanel;
  /** Set the active panel (pass `null` to clear). */
  setActivePanel: (panel: SidebarPanel) => void;
  /** Toggle the given panel — opens if closed, closes if already active. */
  togglePanel: (panel: Exclude<SidebarPanel, null>) => void;
  /** Close any open panel. */
  closePanel: () => void;
}

export const useSidebarPanelStore = create<SidebarPanelState>((set, get) => ({
  activePanel: null,
  setActivePanel: (panel) => set({ activePanel: panel }),
  togglePanel: (panel) => {
    const current = get().activePanel;
    set({ activePanel: current === panel ? null : panel });
  },
  closePanel: () => set({ activePanel: null }),
}));
